"use client";

import { cn } from "@gettactic/helpers";
import React, { useCallback, useEffect, useState } from "react";
import { ColorResult, SketchPicker } from "react-color";
import { Label } from "../../primitives/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "../../primitives/Popover";

export interface ColorPickerInputProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const PRESET_COLORS = [
  "#000000",
  "#374151",
  "#6B7280",
  "#9CA3AF",
  "#D1D5DB",
  "#FFFFFF",
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#EAB308",
  "#84CC16",
  "#22C55E",
  "#10B981",
  "#14B8A6",
  "#06B6D4",
  "#0EA5E9",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#D946EF",
  "#EC4899",
  "#F43F5E",
  "#78716C"
];

export const ColorPickerInput: React.FC<ColorPickerInputProps> = ({
  value = "",
  onChange,
  label,
  placeholder = "#000000",
  error,
  disabled = false,
  className
}) => {
  const [currentColor, setCurrentColor] = useState(value);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setCurrentColor(value);
  }, [value]);

  const handleColorChange = useCallback(
    (color: ColorResult) => {
      const hexColor = color.hex.toUpperCase();
      setCurrentColor(hexColor);
      onChange?.(hexColor);
    },
    [onChange]
  );

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && <Label>{label}</Label>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2",
              disabled && "cursor-not-allowed opacity-50",
              error && "border-destructive",
              !disabled && "cursor-pointer hover:border-neutral-400"
            )}
          >
            <div
              className={cn(
                "h-6 w-6 shrink-0 rounded border border-neutral-300",
                disabled && "cursor-not-allowed"
              )}
              style={{ backgroundColor: currentColor || "#FFFFFF" }}
            />
            <span
              className={cn(
                "flex-1 text-left text-sm",
                !currentColor && "text-muted-foreground"
              )}
            >
              {currentColor || placeholder}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto border-none bg-transparent p-0 shadow-none"
          align="start"
        >
          <SketchPicker
            color={currentColor || "#000000"}
            onChange={handleColorChange}
            presetColors={PRESET_COLORS}
            disableAlpha
          />
        </PopoverContent>
      </Popover>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};
